import { useCallback, useEffect, useMemo, useState } from "react";
import {
  List,
  TextField,
  WrapperField,
  DatagridConfigurable,
  WithRecord,
  DateField,
  BooleanField,
  NumberField,
  BulkDeleteWithConfirmButton,
  FunctionField,
  useTranslate,
  useResourceContext,
  RecordContextProvider,
  ChipField,
  useTheme,
  usePermissions,
  useGetIdentity,
} from "react-admin";
import { useLocation, useNavigate } from "react-router-dom";
import MutationRepackingAddDialog from "../../components/mutationRepackings/create/MutationRepackingAddDialog";
import MutationRepackingEditDialog from "../../components/mutationRepackings/edit/MutationRepackingEditDialog";
import mutationRepackingFilterList from "../../components/mutationRepackings/filter/mutationRepackingFilterList";
import TableListActions from "../../components/tableListActions/TableListActions";
import MutationRepackingExpandList from "../../components/mutationRepackings/expand/MutationRepackingExpandList";
import EditBtn from "../../components/editBtn/EditBtn";
import ConfirmBtn from "../../components/confirmBtn/ConfirmBtn";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import PrintBtn from "../../components/printBtn/PrintBtn";
import PrintHandler from "../../components/printHandler/PrintHandler";
import PrintContent from "../../components/mutationRepackings/print/PrintContent";
import MutationRepackingFooterList from "../../components/mutationRepackings/footer/MutationRepackingFooterList";
import MyDatagridBody from "../../components/common/MyDatagridBody";
import getFirstAndLastDateOfCurrentMonth from "../../utils/firstLastDate";
import ResetFilterButton from "../../components/tableListActions/ResetFilterButton";

const [firstDate, lastDate] = getFirstAndLastDateOfCurrentMonth();

const MutationRepackingList = () => {
  const resource = useResourceContext();
  const { permissions } = usePermissions();
  const { data: identity } = useGetIdentity();
  const navigate = useNavigate();
  const location = useLocation();
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [recordId, setRecordId] = useState();
  const [resetPrintContent, setResetPrintContent] = useState();
  const [printRecord, setPrintRecord] = useState({}); // initial value wajib object kosong untuk cek key id di dependency useeffect DeliveryNote.js
  const t = useTranslate();
  const [theme] = useTheme("light");

  const currentCompanyFilter = useMemo(
    () => (identity?.companyID ? { company_id: identity.companyID } : {}),
    [identity?.companyID]
  );

  const locationFilter = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);

    try {
      return JSON.parse(searchParams.get("filter") || "{}");
    } catch {
      return {};
    }
  }, [location.search]);

  useEffect(() => {
    if (!currentCompanyFilter.company_id) return;
    if (locationFilter.company_id === currentCompanyFilter.company_id) return;

    const searchParams = new URLSearchParams(location.search);
    searchParams.set(
      "filter",
      JSON.stringify({
        ...locationFilter,
        company_id: currentCompanyFilter.company_id,
      })
    );

    navigate(
      {
        pathname: location.pathname,
        search: `?${searchParams.toString()}`,
      },
      { replace: true }
    );
  }, [
    currentCompanyFilter.company_id,
    location.pathname,
    location.search,
    locationFilter,
    navigate,
  ]);

  const filterDefaultValues = useMemo(
    () => ({
      start_date: firstDate,
      end_date: lastDate,
      ...currentCompanyFilter,
    }),
    [currentCompanyFilter]
  );

  const parentFilter = useMemo(
    () => ({
      ...locationFilter,
      ...(currentCompanyFilter.company_id
        ? { company_id: currentCompanyFilter.company_id }
        : {}),
      start_date: locationFilter.start_date || firstDate,
      end_date: locationFilter.end_date || lastDate,
    }),
    [currentCompanyFilter.company_id, locationFilter]
  );

  const handleClickOpenAdd = useCallback(() => {
    setOpenAdd(true);
  }, []);

  const handleClickOpenEdit = useCallback(
    (recId) => () => {
      setRecordId(recId);
      setOpenEdit(true);
    },
    []
  );

  const list = useMemo(
    () => (
      <List
        title="mine.list_title.mutation_repacking"
        storeKey={false}
        empty={false}
        actions={
          <TableListActions
            createHandler={handleClickOpenAdd}
            exporter={false}
            createPermission="create_mutation_repacking"
            extraActions={
              <ResetFilterButton defaultFilters={filterDefaultValues} />
            }
          />
        }
        filters={mutationRepackingFilterList(permissions)}
        filter={parentFilter}
        filterDefaultValues={filterDefaultValues}
        queryOptions={{ meta: { sort: ["created_at DESC"] } }} // overide default sort
      >
        <DatagridConfigurable
          body={<MyDatagridBody footer={<MutationRepackingFooterList />} />}
          bulkActionButtons={<BulkDeleteWithConfirmButton />}
          expand={<MutationRepackingExpandList />}
          sx={{
            "& .RaDatagrid-expandedPanel > td": {
              p: 0,
              bgcolor: theme !== "dark" ? "#E7EBF0" : "grey.800",
            },
          }}
        >
          <TextField source="company.alias" label="mine.label.depo" />
          <TextField source="code" label="mine.label.receipt_no" />
          <DateField
            source="date"
            label="mine.label.date"
            options={{
              year: "numeric",
              month: "short",
              day: "2-digit",
            }}
          />
          <FunctionField
            label="mine.label.type"
            textAlign="center"
            render={(record) => {
              let rec = {};
              if (record.type === 1) {
                rec.type = t("mine.label.stocktake");
                rec.color = "primary";
              } else if (record.type === 2) {
                rec.type = t("mine.label.sample");
                rec.color = "success";
              }
              return (
                <RecordContextProvider value={rec}>
                  <ChipField source="type" color={rec.color} size="small" />
                </RecordContextProvider>
              );
            }}
          />
          <TextField
            source="delivery_note"
            label="mine.label.delivery_note"
            emptyText="-"
          />
          <NumberField source="quantity" />
          <NumberField source="total" />
          <TextField source="note" label="mine.label.note" />
          <TextField source="creator_name" label="mine.label.created_by" />
          <DateField
            source="created_at"
            showTime
            label="mine.label.created_at"
          />
          <BooleanField
            source="approved"
            label="mine.label.approved"
            textAlign="center"
          />
          <WrapperField label="mine.label.actions" textAlign="center">
            <WithRecord
              render={(record) => {
                return !record.approved ? (
                  <>
                    <EditBtn
                      editHandler={handleClickOpenEdit(record.id)}
                      sx={{ mr: 1 }}
                    />
                    <ConfirmBtn
                      title="mine.message.approve"
                      content="mine.message.approve_content"
                      label="mine.btn.approve"
                      resource={resource + "/approve"}
                      startIcon={<DoneIcon />}
                    />
                  </>
                ) : (
                  <>
                    <PrintBtn
                      dialogProps={{ title: "mine.dialog.print" }}
                      recordId={record.id}
                      setPrintRecord={setPrintRecord}
                      setResetPrintContent={setResetPrintContent}
                      btnProps={{ sx: { mx: 1 } }}
                    />
                    {permissions?.administrator ? (
                      <ConfirmBtn
                        title="mine.message.unapprove"
                        content="mine.message.unapprove_content"
                        label="mine.label.unapprove"
                        resource={resource + "/unapprove"}
                        startIcon={<CloseIcon />}
                        color="error"
                        sx={{ ml: 0 }}
                      />
                    ) : null}
                  </>
                );
              }}
            />
          </WrapperField>
        </DatagridConfigurable>
      </List>
    ),
    [
      filterDefaultValues,
      handleClickOpenAdd,
      handleClickOpenEdit,
      parentFilter,
      permissions,
      resource,
      t,
      theme,
    ]
  );

  return (
    <>
      {list}
      <MutationRepackingAddDialog open={openAdd} setOpen={setOpenAdd} />
      <MutationRepackingEditDialog
        open={openEdit}
        setOpen={setOpenEdit}
        recordId={recordId}
      />
      <PrintHandler
        record={printRecord}
        resetPrintContent={resetPrintContent}
        Content={PrintContent}
      />
    </>
  );
};

export default MutationRepackingList;
